import React, { useState, useRef } from 'react';
import { phoneCountries } from '@/constants/phoneCountries';
import { useTranslation } from 'react-i18next';

// Explicitly import DOM types to satisfy the linter
type HTMLDivElement = globalThis.HTMLDivElement;
type HTMLInputElement = globalThis.HTMLInputElement;
type MouseEvent = globalThis.MouseEvent;
type Node = globalThis.Node;

type Props = {
  value: string;
  onChange: (val: string) => void;
  name?: string;
};

function parsePhone(str: string) {
  if (typeof str !== 'string') str = '';
  const match = str.match(/^(\+\d{1,3})\s?(.*)$/);
  if (match) {
    return { code: match[1], number: match[2] };
  }
  // Default Argentina
  return { code: '+54', number: str.replace(/^\+?\d{1,3}\s?/, '') };
}

export const PhoneInputCustom: React.FC<Props> = ({ value, onChange, name }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const { code, number } = parsePhone(value);
  const selected = phoneCountries.find((c) => c.dial === code) || phoneCountries[0];

  // Cierra el dropdown si se hace click fuera
  React.useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    if (open) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const handleCountrySelect = (c: (typeof phoneCountries)[0]) => {
    setOpen(false);
    onChange(`${c.dial} ${number}`.trim());
  };
  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Only allow numbers
    const numericValue = e.target.value.replace(/[^0-9]/g, '');
    onChange(`${code} ${numericValue}`.trim());
  };

  return (
    <div
      ref={ref}
      className="flex items-center border-2 border-gray-300 rounded-md bg-gray-10 shadow-sm focus-within:border-brand-orange focus-within:ring-1 focus-within:ring-brand-orange h-10  w-full relative"
    >
      {/* Dropdown de país */}
      <button
        type="button"
        className="flex items-center mr-2 px-1 py-1 rounded focus:outline-none focus:ring-2 focus:ring-brand-orange"
        onClick={() => setOpen((v) => !v)}
        tabIndex={0}
      >
        <img
          src={selected.flag}
          alt={selected.name}
          className="w-6 h-6 rounded-full shadow"
          style={{ objectFit: 'cover', background: '#fff' }}
        />
        <span className="ml-1 font-medium text-base text-gray-700">{selected.dial}</span>
        <svg
          className="ml-1 w-4 h-4 text-gray-400"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <div className="absolute z-50 left-0 top-12 bg-white border border-gray-200 rounded shadow-lg w-full max-h-56 overflow-auto">
          {phoneCountries.map((c) => (
            <button
              type="button"
              key={c.code}
              className={`flex items-center w-full px-3 py-2 hover:bg-gray-100 ${c.dial === code ? 'bg-gray-50 font-bold' : ''}`}
              onClick={() => handleCountrySelect(c)}
            >
              <img src={c.flag} alt={c.name} className="w-5 h-5 rounded-full mr-2" />
              <span className="mr-2">{c.dial}</span>
              <span className="text-gray-600 text-sm">{c.name}</span>
            </button>
          ))}
        </div>
      )}
      {/* Input de número */}
      <input
        type="tel"
        name={name}
        value={number}
        onChange={handleNumberChange}
        placeholder={t('pages.users.placeholders.phoneNumber')}
        className="flex-1 bg-transparent border-none focus:ring-0 focus:outline-none text-base h-full pl-2"
        autoComplete="off"
        style={{ minWidth: 0 }}
      />
    </div>
  );
};
